import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from 'src/lib/utils';
import type { AdminOrgManagementQuery } from 'src/generated/graphql';
import { useAdminUnbanOrganizationMutation } from 'src/generated/graphql';

type BannedOrganization = NonNullable<
  AdminOrgManagementQuery['adminDashboard']
>['bannedOrganizations'][0];

interface BannedOrgsListProps {
  organizations: BannedOrganization[];
  onUnban: () => void;
}

export const BannedOrgsList = ({
  organizations,
  onUnban,
}: BannedOrgsListProps) => {
  const [unbanningSlug, setUnbanningSlug] = useState<string | null>(null);

  const [unbanOrganization, { loading: unbanLoading }] =
    useAdminUnbanOrganizationMutation({
      onCompleted: () => {
        setUnbanningSlug(null);
        onUnban();
      },
    });

  const handleUnban = (slug: string) => {
    setUnbanningSlug(slug);
    unbanOrganization({ variables: { organizationSlug: slug } });
  };

  if (organizations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        No banned organizations.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Organization
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Slug
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tier
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Banned
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {organizations.map((org) => (
            <tr key={org.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-sm font-medium text-gray-900">
                    {org.name || '(unnamed)'}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                {org.slug}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={cn(
                    'px-2 py-0.5 rounded text-xs font-medium',
                    org.tier === 'LEGACY_FREE'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  )}
                >
                  {org.tier}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {org.bannedAt
                  ? formatDistanceToNow(new Date(org.bannedAt), {
                      addSuffix: true,
                    })
                  : '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <button
                  onClick={() => handleUnban(org.slug)}
                  disabled={unbanLoading && unbanningSlug === org.slug}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 text-xs font-medium"
                >
                  {unbanLoading && unbanningSlug === org.slug
                    ? 'Unbanning...'
                    : 'Unban'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
